import { createContext, useContext, type ReactNode } from 'react';
import type { CoreAPI, CommonSDK } from '@hero/sdk-core';
import type { CustomerApiInt } from '@hero/sdk-customer';
import type { CarrierApiInt } from '@hero/sdk-carrier';
import type { AdminApiInt } from '@hero/sdk-admin';

export type ApiScope = 'customer' | 'carrier' | 'admin';
export type APIClient = CustomerApiInt | CarrierApiInt;

const ApiContext = createContext<CoreAPI | null>(null);

interface ApiProviderProps {
  api: CoreAPI;
  children: ReactNode;
}

export function ApiProvider({ api, children }: ApiProviderProps) {
  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
}

/**
 * Returns the SDK instance provided by `<ApiProvider>`.
 *
 * - `useApi()` (no scope) returns `CommonSDK` — shared methods available on
 *   every role SDK. Use this in shared hooks for common endpoints.
 * - `useApi('carrier')` / `useApi('customer')` / `useApi('admin')` narrows
 *   to the full role-specific type for role-only endpoints.
 */
export function useApi(): CommonSDK;
export function useApi(scope: 'customer'): CustomerApiInt;
export function useApi(scope: 'carrier'): CarrierApiInt;
export function useApi(scope: 'admin'): AdminApiInt;
export function useApi(_scope?: ApiScope): CommonSDK | AdminApiInt {
  const api = useContext(ApiContext);
  if (!api) {
    throw new Error('useApi must be used within an <ApiProvider>');
  }
  return api as CommonSDK | AdminApiInt;
}
